/**
 * "No access" page — what a user sees when they reach a route their workspace
 * role does not cover (Plan-RBAC-SchemaRoles-2026-08.md §9.1, §11.4).
 *
 * 🚨 Deliberately a rendered PAGE, not a redirect. A redirect strands anyone
 * whose role set matches nothing: `/dashboard` bounces to `/dashboard`, and the
 * user ends up in a loop with no way to reach Settings or sign out. Rendering
 * in place keeps the Layout chrome — sidebar, workspace switcher, logout —
 * mounted and usable.
 *
 * Renders inside <Layout>, so no chrome of its own.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Settings as SettingsIcon, LayoutDashboard } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

interface NoAccessPanelProps {
  /** Page name for the copy, when known (e.g. "Knowledge Hub"). */
  pageName?: string;
  /**
   * Overrides the active workspace's name. Only used to render this panel
   * outside a real session (isolated screenshot harness); production callers
   * omit it and the name comes from context.
   */
  workspaceName?: string;
}

const NoAccessPanel: React.FC<NoAccessPanelProps> = ({ pageName, workspaceName }) => {
  const { activeWorkspace } = useWorkspace();
  const wsName = workspaceName || activeWorkspace?.name || 'this workspace';

  return (
    <div
      className="flex items-center justify-center px-2 py-8"
      style={{ minHeight: 'calc(100dvh - 12rem)' }}
    >
      {/* 🚨 Not `glass`. In light mode that utility is rgba(255,255,255,0.1)
          with an rgba(255,255,255,0.2) border — over this page's own
          gray-50/blue-50 gradient it has neither a visible surface nor a
          visible edge, so the panel disappears and the copy floats loose on
          the background. Rendered at 375px to confirm. These are the same
          surface tokens as the `.card` utility (minus its hover shadow, which
          a non-interactive panel should not have), so it reads as a real card
          in both themes. */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl rounded-2xl p-6 sm:p-10 text-center w-full max-w-md">
        <div className="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-5">
          <ShieldAlert className="w-7 h-7 text-amber-600 dark:text-amber-400" />
        </div>

        <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2">
          You don&apos;t have access to this page
        </h1>

        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-1">
          {pageName ? (
            <>
              Your role in{' '}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {wsName}
              </span>{' '}
              doesn&apos;t include <span className="font-medium">{pageName}</span>.
            </>
          ) : (
            <>
              Your role in{' '}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {wsName}
              </span>{' '}
              doesn&apos;t include this page.
            </>
          )}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-7">
          Contact your workspace admin if you need it.
        </p>

        {/* Stacked at 375px, side-by-side from sm up. */}
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-center">
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-primary to-purple-600 text-white hover:shadow-lg transition-all"
          >
            <LayoutDashboard className="w-4 h-4" />
            Go to Dashboard
          </Link>
          <Link
            to="/settings"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <SettingsIcon className="w-4 h-4" />
            Settings
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NoAccessPanel;
